import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type { FieldType } from '@elfsight/widget-form-builder/widget';
import {
  commonFieldsSettings,
  FormFieldContainer,
  FormFieldLayout
} from '@elfsight/widget-form-builder/widget';
import { useEmitAPIEvent } from '@elfsight/widget-js-api';
import { formatSuffixNumber } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { API_CALCULATOR_FORM_SERVICE_NAME } from '../../constants';
import { buildFieldIdForField } from '../components/email-submit-popup/utils/build-fieldId';
import { Empty } from '../components/empty';
import { useCalculatorConditionalLogic } from '../conditional-logic-context';
import { useRegisterFormService } from '../use-register-form-service';
import { calculatorFieldComponents } from './calculatorFieldComponents';
import { fieldsStyles } from './fields-styles';

export function Form({ isMobile }: { isMobile?: boolean }) {
  const {
    fieldStyle,
    textColor,
    fieldAccentColor,
    thousandsSeparator,
    decimalSeparator
  } = useSettings();

  const { displayedFields } = useCalculatorConditionalLogic();

  const emitAPIEvent = useEmitAPIEvent();

  const displayedFieldsIDs = useMemo(() => {
    return displayedFields.reduce<Record<string, string>>((acc, item) => {
      if (item.id) {
        acc[item.id] = item?.fieldID || buildFieldIdForField(item);
      }

      return acc;
    }, {});
  }, [displayedFields]);

  const [prevDisplayedFieldsIDs, setPrevDisplayedFieldsIDs] =
    useState<Record<string, string>>(displayedFieldsIDs);

  useEffect(() => {
    const hidden: Record<string, string> = {};
    const shown: Record<string, string> = {};
    let changed = false;

    for (const id in prevDisplayedFieldsIDs) {
      if (!(id in displayedFieldsIDs)) {
        hidden[id] = prevDisplayedFieldsIDs[id];
        changed = true;
      }
    }

    for (const id in displayedFieldsIDs) {
      if (!(id in prevDisplayedFieldsIDs)) {
        shown[id] = displayedFieldsIDs[id];
        changed = true;
      }
    }

    if (changed) {
      requestAnimationFrame(() => {
        emitAPIEvent(
          'displayedFieldsChange',
          [{ hidden, shown }],
          API_CALCULATOR_FORM_SERVICE_NAME
        );

        setPrevDisplayedFieldsIDs(displayedFieldsIDs);
      });
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [displayedFieldsIDs, emitAPIEvent]);

  useRegisterFormService({
    serviceName: API_CALCULATOR_FORM_SERVICE_NAME,
    fields: displayedFields,
    fieldsIDs: displayedFieldsIDs
  });

  const fieldStyleParameters = fieldsStyles[fieldStyle];
  const hintInsideTypes: FieldType[] = ['choice', 'imageChoice'];

  const headingFieldProps = {
    fieldHeadingFontStyle: {
      fontSize: isMobile ? 22 : 24,
      fontWeight: 'bold'
    },
    fieldHeadingColor: textColor
  };

  if (displayedFields.length === 0) {
    return <Empty _textColor={textColor}>No fields to show</Empty>;
  }

  return (
    <Container>
      {/* @TODO try to fix TS */}
      {displayedFields.map((field: any) => {
        const Component =
          calculatorFieldComponents[
            field.type as keyof typeof calculatorFieldComponents
          ];

        const numberFormatter = (value: number) => {
          return formatSuffixNumber(value, {
            prefix: field.format?.prefix,
            suffix: field.format?.suffix,
            maximumFractionDigits: field.format?.decimalPlaces || 5,
            thousandsSeparator,
            decimalSeparator
          });
        };

        return (
          <FormFieldContainer key={field.id} id={field.id}>
            <FieldLayout
              {...field}
              fieldLabelFontStyle={
                field.type === 'heading'
                  ? fieldStyleParameters.heading
                  : fieldStyleParameters.fieldLabel
              }
              fieldLabelColor={textColor}
              fieldFontStyle={
                ['choice'].includes(field.type)
                  ? commonFieldsSettings.fieldFontStyle
                  : fieldStyleParameters.fieldValue
              }
              fieldBorderType={
                ['choice'].includes(field.type)
                  ? null
                  : fieldStyleParameters.fieldStyle === 'outline'
                    ? 'outline'
                    : null
              }
              fieldFillColor={
                fieldStyleParameters.fieldStyle === 'outline' &&
                field.type !== 'choice'
                  ? 'transparent'
                  : getColorWithAlpha(textColor, 0.05)
              }
              fieldBorderColor={getColorWithAlpha(textColor, 0.2)}
              fieldBorderWidth={1}
              fieldBorderRadius={4}
              required={false}
              fieldColor={textColor}
              fieldActiveColor={fieldAccentColor} // active color
              fieldInsideLabelColor={textColor}
              fieldHintColor={getColorWithAlpha(textColor, 0.7)}
              fieldLabelOutsideStyle={['slider'].includes(field.type)}
              fieldLabelPosition={
                ['slider'].includes(field.type) ? 'inside' : 'outside'
              }
              fieldInsideLabelFontStyle={
                commonFieldsSettings.fieldInsideLabelFontStyle
              }
              fieldSpacing={32}
              fieldPadding={0}
              fieldHintInsideTypes={hintInsideTypes}
            >
              <Component
                {...field}
                {...(field.type === 'heading' ? headingFieldProps : {})}
                decimalSeparator={decimalSeparator}
                numberFormatter={numberFormatter}
              />
            </FieldLayout>
          </FormFieldContainer>
        );
      })}
    </Container>
  );
}

const Container = styled.form`
  display: block;
`;

const FieldLayout = styled(FormFieldLayout)`
  input,
  input:focus {
    background: transparent !important;
    border: none !important;
    font-size: inherit !important;
    line-height: inherit !important;
    padding: 0 !important;
    box-shadow: none !important;
    color: inherit !important;
    min-height: unset !important;
    margin: 0 !important;
  }
`;
